import { SimpleModel } from 'model/SimpleModel';

export namespace AsyncModel {
  export type Status = 'VOID' | 'PENDING' | 'RESOLVED' | 'REJECTED';

  export interface Shape<A> {
    asyncStatuses?: Map<A, Status>;
    asyncResults?: Map<A, unknown>;
    asyncOptimisticResults?: Map<A, unknown>;
    asyncErrors?: Map<A, unknown>;
  }
}

export class AsyncModel<
  T,
  A = string,
  S extends AsyncModel.Shape<A> = AsyncModel.Shape<A>,
> extends SimpleModel<T & S> {
  static readonly VOID: AsyncModel.Status = 'VOID';
  static readonly PENDING: AsyncModel.Status = 'PENDING';
  static readonly RESOLVED: AsyncModel.Status = 'RESOLVED';
  static readonly REJECTED: AsyncModel.Status = 'REJECTED';

  private updateMapField<K extends keyof S, KA, VA>(
    key: K,
    updater: (map: Map<KA, VA>) => void
  ): this {
    // Retrieve the value for the key
    const original = this.get(key);

    // Check if the original value is a Map, and cast it to Map<KA, VA> if true
    if (!(original instanceof Map)) {
      throw new Error(`The value for key "${String(key)}" is not a Map.`);
    }

    // Create a copy of the Map to update
    const copy = new Map(original as Map<KA, VA>);

    // Apply the updater function to the copy of the Map
    updater(copy);

    // Safe casting to (T & S)[K] since we know it's a Map after the check
    return this.set(key, copy as unknown as (T & S)[K]);
  }

  setAsyncStatus(action: A, status: AsyncModel.Status): this {
    return this.updateMapField('asyncStatuses', (map) => map.set(action, status));
  }

  setAsyncResult(action: A, result: unknown): this {
    return this.updateMapField('asyncResults', (map) => map.set(action, result));
  }

  updateAsyncResult<U>(action: A, updater: (data: U) => U, notSetResult?: U): this {
    return this.updateMapField('asyncResults', (map) => {
      const current = (map.get(action) ?? notSetResult) as U;
      map.set(action, updater(current));
    });
  }

  setAsyncError(action: A, error: unknown): this {
    return this.updateMapField('asyncErrors', (map) => map.set(action, error));
  }

  removeAsyncResult(action: A): this {
    return this.updateMapField('asyncResults', (map) => map.delete(action));
  }

  removeAsyncError(action: A): this {
    return this.updateMapField('asyncErrors', (map) => map.delete(action));
  }

  getAsyncStatus(action: A, fallback: AsyncModel.Status = AsyncModel.VOID): AsyncModel.Status {
    return this.get('asyncStatuses')?.get(action) ?? fallback;
  }

  getAsyncResult<U = unknown>(action: A, fallback?: U): U {
    return (this.get('asyncResults')?.get(action) as U) ?? fallback;
  }

  getAsyncOptimisticResult<U = unknown>(action: A, fallback?: U): U {
    return (
      (this.get('asyncOptimisticResults')?.get(action) as U) ??
      this.getAsyncResult(action, fallback)
    );
  }

  getAsyncError(action: A, fallback?: unknown): unknown {
    return this.get('asyncErrors')?.get(action) ?? fallback;
  }

  hasAsyncStatus(action: A, status: AsyncModel.Status): boolean {
    return this.getAsyncStatus(action) === status;
  }

  hasAsyncResult(action: A): boolean {
    return this.get('asyncResults')?.has(action) ?? false;
  }

  hasAsyncError(action: A): boolean {
    return this.get('asyncErrors')?.has(action) ?? false;
  }

  beginAsync(action: A, optimisticResult?: T): this {
    return this.withMutations((data) => {
      const statuses = data['asyncStatuses'] ?? new Map<A, AsyncModel.Status>();
      statuses.set(action, AsyncModel.PENDING);
      data['asyncStatuses'] = statuses;

      if (optimisticResult !== undefined) {
        const optimistic = data['asyncOptimisticResults'] ?? new Map<A, unknown>();
        optimistic.set(action, optimisticResult);
        data['asyncOptimisticResults'] = optimistic;
      }
    });
  }

  resolveAsync<U = unknown>(action: A, result?: U): this {
    return this.withMutations((data) => {
      const statuses = data['asyncStatuses'] ?? new Map<A, AsyncModel.Status>();
      statuses.set(action, AsyncModel.RESOLVED);
      data['asyncStatuses'] = statuses;

      if (result !== undefined) {
        const results = data['asyncResults'] ?? new Map<A, unknown>();
        results.set(action, result);
        data['asyncResults'] = results;
      }

      const optimistic = data['asyncOptimisticResults'] ?? new Map<A, unknown>();
      optimistic.delete(action);
      data['asyncOptimisticResults'] = optimistic;
    });
  }

  rejectAsync(action: A, error?: unknown): this {
    return this.withMutations((data) => {
      const statuses = data['asyncStatuses'] ?? new Map<A, AsyncModel.Status>();
      statuses.set(action, AsyncModel.REJECTED);
      data['asyncStatuses'] = statuses;

      if (error !== undefined) {
        const errors = data['asyncErrors'] ?? new Map<A, unknown>();
        errors.set(action, error);
        data['asyncErrors'] = errors;
      }

      const optimistic = data['asyncOptimisticResults'] ?? new Map<A, unknown>();
      optimistic.delete(action);
      data['asyncOptimisticResults'] = optimistic;

      const results = data['asyncResults'] ?? new Map<A, unknown>();
      results.delete(action);
      data['asyncResults'] = results;
    });
  }

  cancelAsync(action: A): this {
    return this.setAsyncStatus(action, AsyncModel.VOID);
  }

  resetAsync(action: A): this {
    return this.withMutations((data) => {
      const statuses = data['asyncStatuses'] ?? new Map<A, AsyncModel.Status>();
      statuses.set(action, AsyncModel.VOID);
      data['asyncStatuses'] = statuses;

      const results = data['asyncResults'] ?? new Map<A, unknown>();
      results.delete(action);
      data['asyncResults'] = results;

      const optimistic = data['asyncOptimisticResults'] ?? new Map<A, unknown>();
      optimistic.delete(action);
      data['asyncOptimisticResults'] = optimistic;

      const errors = data['asyncErrors'] ?? new Map<A, unknown>();
      errors.delete(action);
      data['asyncErrors'] = errors;
    });
  }

  isAsyncVoid(action: A): boolean {
    return this.hasAsyncStatus(action, AsyncModel.VOID);
  }

  isAsyncPending(action: A): boolean {
    return this.hasAsyncStatus(action, AsyncModel.PENDING);
  }

  isAsyncResolved(action: A): boolean {
    return this.hasAsyncStatus(action, AsyncModel.RESOLVED);
  }

  isAsyncRejected(action: A): boolean {
    return this.hasAsyncStatus(action, AsyncModel.REJECTED);
  }

  isAsyncFulfilled(action: A): boolean {
    return this.isAsyncResolved(action) && this.hasAsyncResult(action);
  }
}
